import React, { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as fbSignOut,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db, ADMIN_EMAILS, CONTROL_EMAILS } from "../firebase";

const normalizeEmail = (email) => String(email || "").trim().toLowerCase();

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        setUser(fbUser);
        const email = normalizeEmail(fbUser.email);
        const isAdmin = ADMIN_EMAILS.includes(email);
        const hasControls = CONTROL_EMAILS.includes(email);
        // Optimistically set profile so UI doesn't hang on Firestore errors
        let resolvedProfile = {
          uid: fbUser.uid,
          email,
          role: isAdmin ? "admin" : "student",
          status: "active",
          hasControls,
        };
        try {
          const userDoc = await getDoc(doc(db, "users", fbUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            resolvedProfile = {
              uid: fbUser.uid,
              ...data,
              email,
              role: isAdmin ? "admin" : data.role,
              hasControls,
            };
          } else {
            // Create profile doc (best-effort; failures are non-fatal)
            try {
              await setDoc(doc(db, "users", fbUser.uid), {
                email,
                role: isAdmin ? "admin" : "student",
                status: "active",
                hasControls,
                createdAt: serverTimestamp(),
              }, { merge: true });
            } catch (e) {
              console.warn("Profile create failed (non-fatal):", e?.message);
            }
          }
        } catch (e) {
          console.warn("Profile fetch failed (non-fatal):", e?.message);
        }
        setProfile(resolvedProfile);
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return cred.user;
  };

  // Admin sign-in: if user doesn't exist & email is in ADMIN_EMAILS, auto-create
  const adminLogin = async (email, password) => {
    const cleanEmail = normalizeEmail(email);
    const hasControls = CONTROL_EMAILS.includes(cleanEmail);
    if (!ADMIN_EMAILS.includes(cleanEmail)) {
      throw new Error("This email is not authorized as admin.");
    }
    try {
      const cred = await signInWithEmailAndPassword(auth, cleanEmail, password);
      const existing = await getDoc(doc(db, "users", cred.user.uid));
      if (existing.exists() && existing.data()?.status === "disabled") {
        await fbSignOut(auth);
        throw new Error("This admin account is disabled.");
      }
      // ensure admin role set
      await setDoc(
        doc(db, "users", cred.user.uid),
        { email: cleanEmail, role: "admin", status: "active", hasControls, createdAt: serverTimestamp() },
        { merge: true }
      );
      return cred.user;
    } catch (err) {
      if (err.code === "auth/user-not-found" || err.code === "auth/invalid-credential") {
        // Try create only if user doesn't exist
        try {
          const cred = await createUserWithEmailAndPassword(auth, cleanEmail, password);
          await setDoc(doc(db, "users", cred.user.uid), {
            email: cleanEmail,
            role: "admin",
            status: "active",
            hasControls,
            createdAt: serverTimestamp(),
          });
          return cred.user;
        } catch (e2) {
          throw err;
        }
      }
      throw err;
    }
  };

  const signOut = async () => {
    await fbSignOut(auth);
  };

  const value = { user, profile, loading, login, adminLogin, signOut };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
